import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom"; // Add useNavigate
import { Outlet } from "react-router";
import {
  isAuthorizedUser,
  signInWithGitHub,
  signOut,
} from "../services/supabase";
import { useSiteSettings } from "../context/site-settings";
import SiteTerminal from "../components/other/site-terminal";

function MainLayout() {
  const navigate = useNavigate(); // Add this
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { settings } = useSiteSettings();

  useEffect(() => {
    const checkAuth = async () => {
      const authorized = await isAuthorizedUser();
      setIsAdmin(authorized);
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const handleAuth = async () => {
    if (isAdmin) {
      await signOut();
      setIsAdmin(false);
      navigate("/"); // Add this line to redirect to home
    } else {
      await signInWithGitHub();
    }
  };

  const AuthButton = () => (
    <button
      onClick={handleAuth}
      disabled={isLoading}
      className={`
        flex items-center gap-2 px-4 py-2 
        border transition-all duration-200
        ${
          isAdmin
            ? "border-red-500/20 hover:border-red-500/40 text-red-400 hover:text-red-300"
            : "border-white/20 hover:border-white/40 text-white/90 hover:text-white"
        }
        ${isLoading ? "opacity-50 cursor-wait" : ""}
      `}
    >
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
      </svg>
      {isAdmin ? "Sign Out" : "Sign In"}
    </button>
  );

  return (
    <div className="min-h-screen w-full bg-black text-white font-mono flex flex-col overflow-x-clip [&_h1]:font-mono">
      <nav className="bg-black p-4 border-b border-white fixed top-0 left-0 right-0 z-50">
        <div className="w-full max-w-7xl mx-auto flex items-center min-w-0">
          {/* Logo - Left */}
          <div className="flex-none">
            <Link
              to="/"
              className="text-lg sm:text-xl font-bold hover:text-gray-400 whitespace-nowrap"
            >
              Emil's Portfolio
            </Link>
          </div>

          {/* Navigation Links - Center */}
          <div className="hidden md:flex flex-grow justify-center">
            <ul className="flex space-x-1 items-center">
              <li>
                <Link
                  to="/"
                  className="px-4 py-2 hover:bg-white hover:text-black transition-colors"
                >
                  Home
                </Link>
              </li>
              {settings.blog_enabled && (
                <li>
                  <Link
                    to="/blog"
                    className="px-4 py-2 hover:bg-white hover:text-black transition-colors"
                  >
                    Blog
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/projects"
                  className="px-4 py-2 hover:bg-white hover:text-black transition-colors"
                >
                  Projects
                </Link>
              </li>
              <li>
                <Link
                  to="/resume"
                  className="px-4 py-2 hover:bg-white hover:text-black transition-colors"
                >
                  Resume
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="px-4 py-2 hover:bg-white hover:text-black transition-colors"
                >
                  About
                </Link>
              </li>
            </ul>
          </div>

          {/* Admin and Auth - Right */}
          <div className="hidden md:flex flex-none items-center gap-4">
            <button
              onClick={() =>
                window.dispatchEvent(new CustomEvent("site-terminal:toggle"))
              }
              title="Open terminal (key below ESC)"
              aria-label="Open terminal"
              className="px-3 py-2 border border-white/20 hover:border-green-400/60
                         text-white/60 hover:text-green-400 transition-all duration-200"
            >
              &gt;_
            </button>
            {isAdmin && (
              <Link
                to="/admin"
                className="px-4 py-2 border border-yellow-500/20 text-yellow-500 
                         hover:border-yellow-500/40 hover:text-yellow-400 
                         transition-all duration-200"
              >
                Admin Panel
              </Link>
            )}
            <AuthButton />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() =>
              window.dispatchEvent(new CustomEvent("site-terminal:toggle"))
            }
            aria-label="Open terminal"
            className="md:hidden ml-auto mr-4 text-white/60 hover:text-green-400 font-mono"
          >
            &gt;_
          </button>
          <button
            className="md:hidden text-white text-2xl"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? "×" : "☰"}
          </button>
        </div>
      </nav>

      <div
        className={`
          fixed top-[64px] left-0 bg-black w-full
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-y-0" : "-translate-y-full"}
          md:hidden
          z-40
          overflow-hidden
        `}
        style={{
          transformOrigin: "top",
          clipPath: "inset(0 0 -100% 0)",
          height: "auto",
          maxHeight: "calc(100vh - 64px)",
        }}
      >
        <div className="py-2 border-b border-white">
          <ul className="flex flex-col items-center">
            <li className="w-64">
              {" "}
              <Link
                to="/"
                className="block w-full px-4 py-3 hover:bg-white hover:text-black transition-colors text-center"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
            </li>
            {settings.blog_enabled && (
              <li className="w-64">
                <Link
                  to="/blog"
                  className="block w-full px-4 py-3 hover:bg-white hover:text-black transition-colors text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Blog
                </Link>
              </li>
            )}
            <li className="w-64">
              <Link
                to="/projects"
                className="block w-full px-4 py-3 hover:bg-white hover:text-black transition-colors text-center"
                onClick={() => setIsOpen(false)}
              >
                Projects
              </Link>
            </li>
            <li className="w-64">
              <Link
                to="/resume"
                className="block w-full px-4 py-3 hover:bg-white hover:text-black transition-colors text-center"
                onClick={() => setIsOpen(false)}
              >
                Resume
              </Link>
            </li>
            <li className="w-64">
              <Link
                to="/about"
                className="block w-full px-4 py-3 hover:bg-white hover:text-black transition-colors text-center"
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
            </li>
            {isAdmin && (
              <li className="w-64">
                <Link
                  to="/admin"
                  className="block w-full px-4 py-3 text-center
                           border border-yellow-500/20 text-yellow-500 
                           hover:border-yellow-500/40 hover:text-yellow-400
                           transition-all my-2"
                  onClick={() => setIsOpen(false)}
                >
                  Admin Panel
                </Link>
              </li>
            )}
            <li className="w-64">
              <div className="flex justify-center w-full">
                <button
                  onClick={handleAuth}
                  disabled={isLoading}
                  className={`
                    w-full px-4 py-3 text-center
                    border transition-all duration-200
                    ${
                      isAdmin
                        ? "border-red-500/20 hover:border-red-500/40 text-red-400 hover:text-red-300"
                        : "border-white/20 hover:border-white/40 text-white/90 hover:text-white"
                    }
                    ${isLoading ? "opacity-50 cursor-wait" : ""}
                  `}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                      fill="currentColor"
                    >
                      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                    </svg>
                    {isAdmin ? "Sign Out" : "Sign In"}
                  </div>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 
          ${
            isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          } md:hidden z-30`}
        onClick={() => setIsOpen(false)}
        style={{ top: "64px" }}
        aria-hidden="true"
      />

      <div className="w-full max-w-7xl mx-auto px-4 py-4 flex-grow mt-[64px]">
        <Outlet />
      </div>

      <SiteTerminal />

      <footer className="bg-black p-4 border-t border-white text-center">
        <p>
          &copy; {new Date().getFullYear()} Emil's Portfolio. All rights
          reserved.
        </p>
      </footer>
    </div>
  );
}

export default MainLayout;
